import * as XLSX from 'xlsx';
import { api, mergeFilters } from './analysesUtils';
import { WIDGET_CATALOG } from './widgetCatalog';

const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

// Convertit les données d'un widget en tableau de lignes (AOA) pour Excel.
const widgetToAoa = (type, data) => {
  switch (type) {
    case 'kpis':
      return [
        ['Indicateur', 'Valeur'],
        ['Total dossiers', data.nb_total],
        ['Dossiers TICFE', data.nb_ticfe],
        ['Dossiers TICGN', data.nb_ticgn],
        ['Dossiers TURPE', data.nb_turpe],
        ['Économies totales (€)', round2(data.total_economies)],
        ['Économie moyenne (€)', round2(data.economie_moyenne)],
        ['Dossiers éligibles', data.nb_eligibles],
        ["Taux d'éligibilité (%)", data.taux_eligibilite],
        ['Validés client', data.nb_valides_client],
        ['Économies validées client (€)', round2(data.eco_valides_client)],
        ['Taux de validation (%)', data.taux_validation],
      ];
    case 'dossiers_par_mois':
      return [['Mois', 'TICFE', 'TICGN', 'TURPE'],
        ...(data || []).map(r => [r.mois, r.TICFE || 0, r.TICGN || 0, r.TURPE || 0])];
    case 'economies_par_type':
    case 'economies_par_agence':
    case 'economies_par_commercial':
      return [['Libellé', 'Économies (€)'],
        ...(data || []).map(r => [r.label || 'Non attribué', round2(r.value)])];
    case 'evolution_economies':
      return [['Mois', 'Économies (€)'],
        ...(data || []).map(r => [r.mois, round2(r.economies)])];
    case 'heatmap_commerciaux_mois': {
      const mois = data?.mois || [];
      return [['Commercial', ...mois],
        ...((data?.rows) || []).map(r => [r.commercial, ...mois.map(m => r[m] || 0)])];
    }
    case 'dossiers_a_cloturer':
      return [['Client', 'SIREN', 'Taxe', 'Économies (€)', 'Échéance CERFA', 'Jours restants'],
        ...(data || []).map(r => [r.nom_entreprise, r.siren, r.taxe, round2(r.economies), r.cerfa_echeance, r.jours_restants])];
    case 'liste_dossiers':
      return [['Client', 'SIREN', 'Taxe', 'Économies (€)', 'Éligibilité', 'Validé client', 'Date'],
        ...(data || []).map(r => [r.nom_entreprise, r.siren, r.taxe, round2(r.economies),
          r.eligibilite || '', r.valide_client ? 'Oui' : 'Non', (r.created_at || '').slice(0, 10)])];
    default:
      return [['Donnée'], ['Type de widget non exporté']];
  }
};

// Largeurs de colonnes auto.
const autoCols = (aoa) => {
  const ncol = aoa[0]?.length || 1;
  return Array.from({ length: ncol }, (_, c) => {
    const max = aoa.reduce((m, row) => {
      const v = row[c];
      const len = v == null ? 0 : String(v).length;
      return len > m ? len : m;
    }, 0);
    return { wch: Math.min(Math.max(max + 2, 10), 50) };
  });
};

// Nom de feuille Excel valide (≤31 car, sans caractères interdits, unique).
const sheetName = (base, used) => {
  let name = (base || 'Widget').replace(/[\\/?*[\]:]/g, ' ').slice(0, 28).trim() || 'Widget';
  let n = name, i = 2;
  while (used.has(n)) { n = `${name.slice(0, 26)} ${i++}`; }
  used.add(n);
  return n;
};

const DIM_LABEL = { agence: 'Agence', commercial: 'Commercial', taxe: 'Taxe', mois: 'Mois', eligibilite: 'Éligibilité', valide_client: 'Validation' };

/**
 * Exporte la vue (widgets) telle qu'affichée — filtres globaux + sélection cross-filtre
 * appliqués — dans un classeur Excel multi-onglets.
 */
export const exportAnalysesToExcel = async ({ widgets, globalFilters, selections = [], viewName }) => {
  const wb = XLSX.utils.book_new();
  const used = new Set();

  // 1) Feuille de synthèse des filtres appliqués
  const synth = [['Export du tableau de bord', viewName || 'Vue par défaut'],
    ['Date export', new Date().toLocaleString('fr-FR')], [],
    ['Filtres / sélections appliqués', '']];
  if (selections.length) {
    selections.forEach(s => synth.push([DIM_LABEL[s.dim] || s.dim, s.label || s.id]));
  } else {
    synth.push(['(aucune sélection de cross-filtrage)', '']);
  }
  const wsSynth = XLSX.utils.aoa_to_sheet(synth);
  wsSynth['!cols'] = autoCols(synth);
  XLSX.utils.book_append_sheet(wb, wsSynth, sheetName('Synthèse filtres', used));

  // 2) Une feuille par widget (données filtrées)
  for (const w of widgets) {
    const filters = mergeFilters(globalFilters, w.filters);
    let data = null;
    try {
      const r = await api('/analytics/widget-data', { method: 'POST', body: JSON.stringify({ type: w.type, filters }) });
      data = r.data;
    } catch (e) {
      data = null;
    }
    const aoa = data != null ? widgetToAoa(w.type, data) : [['Erreur de chargement']];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = autoCols(aoa);
    const meta = WIDGET_CATALOG.find(c => c.type === w.type);
    XLSX.utils.book_append_sheet(wb, ws, sheetName(w.label || meta?.label || w.type, used));
  }

  const safe = (viewName || 'dashboard').replace(/[^a-zA-Z0-9-_]/g, '_').slice(0, 40);
  XLSX.writeFile(wb, `analyses_${safe}_${new Date().toISOString().slice(0, 10)}.xlsx`);
};
